import PropTypes from 'prop-types';
import {
  useCallback, useEffect, useMemo, useState
} from 'react';
import { Provider } from './context';
import useActiveEpisode from '@/hooks/audio/useActiveEpisode';
import useLiveAudioSeek from '@/hooks/audio/useLiveAudioSeek';
import { truthyOrZero } from '@/helpers/math';
import useAudioReduxControls from '@/hooks/audio/useAudioReduxControls';
import { getNextTrackFromTracklist, getTrackFromTracklistByTime } from '@/helpers/audio';

const STARTING_TRACK_INDEX = -1;

const AudioTracklistProvider = ({ children }) => {
  const [currentTrackIndex, setCurrentTrackIndex] = useState(STARTING_TRACK_INDEX);

  const {
    episode, episodeKey, tracklist
  } = useActiveEpisode();
  const { handleSaveSeek, handleNextTrack } = useAudioReduxControls();
  const { liveSeek, handleSeek: handleLiveSeek } = useLiveAudioSeek();
  const currentSeekedToTrack = tracklist[currentTrackIndex];

  const {
    track: currentTrack,
    trackText: currentTrackText,
    isHidden: currentTrackIsHidden,
    trackIndex
  } = getTrackFromTracklistByTime({
    episode,
    timeSeconds: liveSeek
  });

  const currentTrackArtist = (currentTrack?.artist || '').trim();
  const currentTrackTitle = (currentTrack?.title || '').trim();

  const { track: nextTrack } = useMemo(() => getNextTrackFromTracklist({
    episode,
    currentTrackIndex
  }), [episode, currentTrackIndex]);

  // Only update the track index if we entered a new valid track.
  // This way we know which track will come next
  useEffect(() => {
    if (liveSeek > 1 && truthyOrZero(trackIndex) && (currentTrackIndex !== trackIndex)) {
      setCurrentTrackIndex(trackIndex);
    }
  }, [trackIndex, currentTrackIndex, liveSeek]);

  // Reset current track index to start when the episode key changes
  useEffect(() => {
    setCurrentTrackIndex(STARTING_TRACK_INDEX);
  }, [episodeKey]);

  // Reset the current track index if we go back to the start of the mix
  useEffect(() => {
    if (liveSeek <= 1 && currentTrackIndex !== STARTING_TRACK_INDEX) {
      setCurrentTrackIndex(STARTING_TRACK_INDEX);
    }
  }, [currentTrackIndex, liveSeek]);

  // Handle skipping the current track in the mix
  const handleSkipTrack = useCallback(() => {
    const { track: next } = getNextTrackFromTracklist({
      episode,
      currentTrackIndex
    });

    const { start_time: nextTrackStartTime } = next || {};

    if (next) {
      setCurrentTrackIndex(currentTrackIndex + 1);
    }

    if (
      truthyOrZero(nextTrackStartTime)
    ) {
      // Skip to next track within this episode
      handleLiveSeek(nextTrackStartTime);
      handleSaveSeek(nextTrackStartTime);
    } else {
      // Skip to next episode
      handleNextTrack();
    }

    // Send GA event to say that we skipped the track
  }, [handleSaveSeek, handleLiveSeek, episode, handleNextTrack, currentTrackIndex]);

  const value = useMemo(() => ({
    handleSkipTrack,
    currentTrack,
    currentTrackArtist,
    currentTrackTitle,
    currentTrackIndex,
    currentTrackText,
    currentTrackIsHidden,
    currentSeekedToTrack,
    nextTrack
  }), [
    handleSkipTrack,
    currentTrack,
    currentTrackArtist,
    currentTrackTitle,
    currentTrackIndex,
    currentTrackText,
    currentTrackIsHidden,
    currentSeekedToTrack,
    nextTrack
  ]);

  return (
    <Provider value={ value }>
      { children }
    </Provider>
  );
};

AudioTracklistProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]).isRequired
};
    
export default AudioTracklistProvider;